/**
 * Created: 2026-04-07
 * Initial commit: Add event dialog for slot page event cell click (feat/event/mr-9)
 *
 * Dialog component for displaying event details and signed-up players
 * when an event-reserved merged cell is clicked in the SlotsPage.
 */

import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  Typography,
} from '@mui/material'
import UserAvatar from '../components/UserAvatar'
import { EventCategory, EventStatus, GetEventForSlotDocument } from '../gql/graphql'
import { formatEventTimeRange } from '../lib/dateUtils'

gql`
  query GetEventForSlot($id: ID!) {
    event(id: $id) {
      id
      name
      location
      description
      startTime
      duration
      drawSize
      numSignups
      fee
      eventType
      status
      requiredRating
      players {
        id
        rank
        user {
          id
          name
          avatar
          mobile
          gender
          ratingSingles
          openid
        }
      }
    }
  }
`

const EVENT_TYPE_CHIP: Record<
  EventCategory,
  { label: string; color: 'primary' | 'secondary' }
> = {
  [EventCategory.Freeplay]: { label: '畅打', color: 'secondary' },
  [EventCategory.Tournament]: { label: '比赛', color: 'primary' },
}

const EVENT_STATUS_LABEL: Record<EventStatus, string> = {
  [EventStatus.Created]: '报名中',
  [EventStatus.Started]: '进行中',
  [EventStatus.Completed]: '已完成',
  [EventStatus.Cancelled]: '已取消',
  [EventStatus.Aborted]: '已中止',
}

const EVENT_STATUS_COLOR: Record<
  EventStatus,
  'default' | 'primary' | 'success' | 'error' | 'warning'
> = {
  [EventStatus.Created]: 'primary',
  [EventStatus.Started]: 'warning',
  [EventStatus.Completed]: 'success',
  [EventStatus.Cancelled]: 'error',
  [EventStatus.Aborted]: 'error',
}

interface EventDialogProps {
  eventId: string | null
  onClose: () => void
}

export default function EventDialog({ eventId, onClose }: EventDialogProps) {
  const { data, loading } = useQuery(GetEventForSlotDocument, {
    variables: { id: eventId as string },
    skip: !eventId,
    fetchPolicy: 'network-only',
  })

  const event = data?.event

  return (
    <Dialog open={!!eventId} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 12 }}>
        {event && (
          <Chip
            label={EVENT_TYPE_CHIP[event.eventType].label}
            color={EVENT_TYPE_CHIP[event.eventType].color}
            size="small"
            sx={{ mr: 0.5, mb: 0.5 }}
          />
        )}
        {event?.name ?? '活动详情'}
        {event && (
          <Chip
            label={EVENT_STATUS_LABEL[event.status]}
            size="small"
            color={EVENT_STATUS_COLOR[event.status]}
            sx={{ position: 'absolute', right: 44, top: 12 }}
          />
        )}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {event && (
          <>
            <Box
              sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}
            >
              <Box sx={{ gridColumn: 'span 2' }}>
                <Typography variant="caption" color="text.secondary">
                  时间
                </Typography>
                <Typography variant="body2">
                  {formatEventTimeRange(event.startTime, event.duration)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  地点
                </Typography>
                <Typography variant="body2">{event.location}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  报名费
                </Typography>
                <Typography variant="body2">
                  {Number(event.fee) === 0 ? '免费' : `¥${event.fee}`}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  报名人数
                </Typography>
                <Typography variant="body2">
                  {event.numSignups} / {event.drawSize}
                </Typography>
              </Box>
              {event.requiredRating != null && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    最低评级
                  </Typography>
                  <Typography variant="body2">
                    {event.requiredRating.toFixed(2)}
                  </Typography>
                </Box>
              )}
            </Box>

            {event.description && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  活动描述
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {event.description}
                </Typography>
              </Box>
            )}

            <Divider sx={{ mb: 1 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              已报名选手 ({event.players.length})
            </Typography>

            {event.players.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                暂无选手报名
              </Typography>
            ) : (
              <List dense disablePadding>
                {event.players.map((player, idx) => (
                  <ListItem key={player.id} disableGutters>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minWidth: 24, textAlign: 'right', mr: 1 }}
                    >
                      {idx + 1}.
                    </Typography>
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <UserAvatar user={player.user} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {player.user.ratingSingles != null && (
                          <Typography variant="caption" color="text.secondary">
                            {player.user.ratingSingles.toFixed(2)}
                          </Typography>
                        )}
                        {player.rank != null && (
                          <Chip
                            label={`第 ${player.rank} 名`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                      </Box>
                    </Box>
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
